import { useCallback, useEffect, useState, useMemo } from 'react';
import { HIDDEN_EVAL_TYPES } from '@/types/eval-v2-types';
import { getEvaluations } from '@/services/evaluation/get-evaluations';
import {
  deleteEvalV2,
  evalV2ToEval,
  getEvalsV2,
  isV2Eval,
} from '@/services/evaluation/eval-v2';
import type { Eval } from '@/types/evals-types';
import { sortByUpdatedAt } from '@/utils/sort-utils';
import { generatePath } from 'react-router';
import { useOrgNavigate } from '@/hooks/use-org-navigate';
import { ROUTES } from '@/constants/routes';
import { useOrgStore } from '@/stores/org-store';
import { PageTitle } from '@/components/page-title';
import { PageDescription } from '@/components/page-description';
import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/search-input';
import { Table } from '@/components/table';
import { ColumnDef } from '@tanstack/react-table';
import { DATE_FORMAT_PATTERN_WITH_TIME } from '@/constants';
import { format } from 'date-fns';
import { modelTypes } from './components/base-eval-config';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MoreVertical, Trash, Play } from 'lucide-react';
import { DeleteConfirmationModal } from '@/components/delete-confirmation-modal';
import { deleteEvaluation } from '@/services/evaluation/delete-evaluation';
import { useNotification } from '@/components/notifications';

// Sample data for initial evals
const initialEvals: Eval[] = [];

// Helper function to get model label
const getModelLabel = (model: string | undefined) => {
  if (!model) return '';
  const currentModel = modelTypes.find((m) => m.id === model);
  return currentModel?.name || model;
};

export default function EvaluationDashboard() {
  const [evals, setEvals] = useState<Eval[]>(initialEvals);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedEvalId, setSelectedEvalId] = useState<string | null>(null);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const navigate = useOrgNavigate();
  const { currentOrg } = useOrgStore();
  const [previousOrgId, setPreviousOrgId] = useState<string | null>(null);
  const notification = useNotification();

  const fetchEvals = useCallback(async () => {
    setIsLoading(true);
    try {
      // v1 and v2 eval-config records live in two different tables. Merge
      // them client-side and discriminate on `type` when routing actions
      // (delete, click-through).
      const [v1Evals, v2Evals] = await Promise.all([
        getEvaluations(),
        getEvalsV2(),
      ]);
      const merged = [...v1Evals, ...v2Evals.map(evalV2ToEval)];
      setEvals(sortByUpdatedAt(merged));
    } catch (error) {
      console.error('Error fetching evaluations:', error);
      setEvals(initialEvals);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Reset evalId parameter when organization changes
  useEffect(() => {
    if (currentOrg) {
      const orgHasChanged =
        previousOrgId !== null && previousOrgId !== currentOrg.orgid;

      if (orgHasChanged) {
        // Navigate to evaluations root to reset evalId parameter
        navigate(ROUTES.EVALUATION.EVALUATIONS);
      }

      setPreviousOrgId(currentOrg.orgid);
    }
  }, [currentOrg, previousOrgId, navigate]);

  useEffect(() => {
    fetchEvals();
  }, [fetchEvals]);

  const handleAddEval = () => {
    navigate(
      generatePath(ROUTES.EVALUATION.EVALUATION_SETTINGS, { evalId: 'new' }),
    );
  };

  const handleRowClick = (evalId: string) => {
    navigate(generatePath(ROUTES.EVALUATION.EVALUATION_SETTINGS, { evalId }));
  };

  const columns: ColumnDef<Eval, unknown>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
    },

    {
      accessorKey: 'type',
      header: 'Type',
      cell: ({ row }) => {
        const type: string = row.original.type ?? '';
        if (type === 'exemplar') return <span>Reference Output</span>;
        if (type === 'pass_fail') return <span>Pass/Fail</span>;
        return (
          <span>
            {type
              .split('_')
              .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
              .join(' ')}
          </span>
        );
      },
    },
    {
      accessorKey: 'model',
      header: 'Model',
      cell: ({ row }) => {
        const modelLabel = getModelLabel(row.original.model);
        return <span>{modelLabel}</span>;
      },
    },
    {
      accessorKey: 'created_at',
      header: 'Created At',
      cell: ({ row }) => {
        return (
          <span>
            {row.original.created_at &&
              format(row.original.created_at, DATE_FORMAT_PATTERN_WITH_TIME)}
          </span>
        );
      },
    },
    {
      accessorKey: 'id',
      header: '',
      cell: ({ row }) => {
        return (
          <div className='flex justify-end'>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant='ghost' size='icon'>
                  <MoreVertical />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end'>
                <DropdownMenuItem
                  variant='destructive'
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedEvalId(row.original.id);
                    setIsDeleteModalOpen(true);
                  }}
                >
                  <Trash />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    },
  ];

  const handleDeleteEval = async () => {
    if (!selectedEvalId) return;
    setIsDeleteLoading(true);
    try {
      const target = evals.find((e) => e.id === selectedEvalId);
      if (target && isV2Eval(target)) {
        await deleteEvalV2(selectedEvalId);
      } else {
        await deleteEvaluation(selectedEvalId);
      }
      notification.success('Evaluation deleted successfully');
      setIsDeleteModalOpen(false);
      setSelectedEvalId(null);
      fetchEvals();
    } catch (error) {
      notification.error('Failed to delete evaluation');
      console.error('Error deleting evaluation:', error);
    } finally {
      setIsDeleteLoading(false);
      setIsDeleteModalOpen(false);
    }
  };

  // Filter evaluations: hide disabled types first, then apply the
  // user's search term.
  const filteredEvals = useMemo(() => {
    const visibleEvals = evals.filter(
      (evaluation) => !HIDDEN_EVAL_TYPES.has(evaluation.type ?? ''),
    );

    if (!searchTerm.trim()) {
      return visibleEvals;
    }

    const lowerSearchTerm = searchTerm.toLowerCase();
    return visibleEvals.filter((evaluation) => {
      const matchesName = evaluation.name
        ?.toLowerCase()
        .includes(lowerSearchTerm);
      const matchesType = evaluation.type
        ?.toLowerCase()
        .includes(lowerSearchTerm);
      const matchesModel =
        evaluation.model &&
        getModelLabel(evaluation.model).toLowerCase().includes(lowerSearchTerm);

      return matchesName || matchesType || matchesModel;
    });
  }, [evals, searchTerm]);

  return (
    <div className='w-full'>
      <div className='mb-6'>
        <div className='flex justify-between items-center'>
          <PageTitle title='Evaluations' />
          <div className='flex items-center gap-2'>
            <Button variant='outline' asChild>
              <a
                href='https://www.loom.com/share/ef654cdc1ec04c4ea4ca8e3ac11926ca?sid=c74b65e0-ef53-436a-8b4c-0a300d438ce6'
                target='_blank'
                rel='noopener noreferrer'
              >
                <Play />
                Help
              </a>
            </Button>
            <Button onClick={handleAddEval}>New evaluation</Button>
          </div>
        </div>
        <PageDescription>
          Set up evaluations to measure how well your generations meet the mark.
          Define what success and failure look like, choose a model to judge the
          results, and add scoring or pass/fail criteria. Use evaluations to
          keep your outputs consistent, reliable, and aligned with your goals.
        </PageDescription>
      </div>
      <div className='mb-6'>
        <div className='max-w-96'>
          <SearchInput
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder='Search evaluations by name, type, or model...'
            aria-label='Search evaluations'
          />
        </div>
      </div>
      <Table
        columns={columns}
        data={filteredEvals}
        loading={isLoading}
        onRowClick={(row) => handleRowClick(row.id)}
      />
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        isLoading={isDeleteLoading}
        title='Delete Evaluation'
        description='Are you sure you want to delete this evaluation? All evaluation results and data will be permanently removed. This action cannot be undone.'
        buttonLabel='Delete Evaluation'
        handleDelete={handleDeleteEval}
        handleCancel={() => setIsDeleteModalOpen(false)}
      />
    </div>
  );
}
